/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { useEffect, useRef } from "react"
import type { KeybindingOptions } from "tinykeys"
import { tinykeys } from "tinykeys"

export interface KeyboardShortcutOptions extends KeybindingOptions {
    /**
     * Element the shortcut listens on.
     * @defaultValue `window`
     */
    target?: Window | HTMLElement

    /**
     * Suppresses the browser default of the matched combination.
     * @defaultValue `true`
     */
    preventDefault?: boolean
}

/**
 * Ignores keyboard events that shortcuts should never react to.
 *
 * @returns `true` for key repeats and IME composition events.
 */
const ignoreRepeatedAndComposed = (event: KeyboardEvent): boolean => event.repeat || event.isComposing

// Mirrors tinykeys' `$mod` platform detection so displayed shortcuts always
// match the binding that is actually registered.
const isApplePlatform = /Mac|iPod|iPhone|iPad/.test(navigator.platform)

const MODIFIER_LABELS = new Map([
    ["$mod", { apple: "⌘", other: "Ctrl" }],
    ["meta", { apple: "⌘", other: "Meta" }],
    ["control", { apple: "⌃", other: "Ctrl" }],
    ["ctrl", { apple: "⌃", other: "Ctrl" }],
    ["alt", { apple: "⌥", other: "Alt" }],
    ["option", { apple: "⌥", other: "Alt" }],
    ["shift", { apple: "⇧", other: "Shift" }]
])

/**
 * Converts a tinykeys binding into user-facing key labels.
 *
 * @param binding - A binding passed to {@link useKeyboardShortcut}, such as `"$mod+k"`.
 * @returns One label per key, with modifiers first (e.g. `["⌘", "K"]` on Apple platforms,
 *   `["Ctrl", "K"]` elsewhere).
 *
 * @remarks
 * Supports simple `modifier+key` bindings. Regex bindings (e.g. `"$mod+(k|KeyK)"`)
 * and sequences (e.g. `"g d"`) are returned largely unchanged.
 */
export const getShortcutLabels = (binding: string): string[] =>
    binding.split("+").map((part) => {
        const modifier = MODIFIER_LABELS.get(part.toLowerCase())
        if (modifier) return isApplePlatform ? modifier.apple : modifier.other

        // tinykeys accepts both KeyboardEvent.key and KeyboardEvent.code (e.g. "k" and "KeyK").
        const key = part.replace(/^Key/, "")
        return key.length === 1 ? key.toUpperCase() : key
    })

/**
 * Registers a keyboard shortcut for the lifetime of the component.
 *
 * @param binding - A tinykeys binding such as `"$mod+k"` (`Cmd` on macOS, `Ctrl` elsewhere).
 * @param handler - Invoked whenever the shortcut matches. The latest handler is always used.
 * @param options - Additional tinykeys options plus `target` and `preventDefault`.
 *
 * @remarks
 * Tinykeys ignores events from form controls by default. Pass
 * `ignore: defaultKeybindingsHandlerIgnore` to opt back in.
 *
 * Options are captured when the subscription is created; changes take effect
 * only after the binding is re-subscribed.
 */
export const useKeyboardShortcut = (
    binding: string,
    handler: () => void,
    options: KeyboardShortcutOptions = {}
): void => {
    const handlerRef = useRef(handler)
    const optionsRef = useRef(options)

    // Avoid re-subscribing while keeping handler/options up to date.
    useEffect(() => {
        handlerRef.current = handler
        optionsRef.current = options
    })

    useEffect(() => {
        const {
            target = window,
            preventDefault = true,
            ignore = ignoreRepeatedAndComposed,
            ...keybindingOptions
        } = optionsRef.current

        return tinykeys(
            target,
            {
                [binding]: (event) => {
                    if (preventDefault) event.preventDefault()
                    handlerRef.current()
                }
            },
            { ignore, ...keybindingOptions }
        )
    }, [binding])
}
